import { Cushion, FlexContainer, Rectangle, Typography } from '@pitchero/react-ui';
import { isNumber } from 'lodash';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import Icon from '../icon';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import ClubRouter from '../../lib/routing';

const NextAndPreviousControls = ({
  eventType,
  month,
  teamId,
  year,
  desiredDay,
  setDesiredDay,
  setDesiredMonth,
  setDesiredYear,
  validDates,
  club,
  placement,
}) => {
  if (
    !validDates ||
    validDates.length === 0 ||
    !isNumber(desiredDay) ||
    !isNumber(year) ||
    !isNumber(month)
  ) {
    return null;
  }

  const first = validDates[0];
  const last = validDates[validDates.length - 1];
  const desiredDateTime = DateTime.fromObject({
    year,
    month,
    day: desiredDay,
  });
  const firstValidDateTime = DateTime.fromFormat(first, 'yyyy-MM-dd');
  const lastValidDateTime = DateTime.fromFormat(last, 'yyyy-MM-dd');

  const wrapPrev = (content) => (
    <button
      type="button"
      onClick={() => {
        // If we go back a week from desired day, are we in the previous month?
        // If so, set the URL to the previous month
        const prevDesired = desiredDateTime.minus({ weeks: 1 });
        const prevMonth = desiredDateTime.minus({ months: 1 });
        let prevYear;

        if (desiredDateTime.month === 1 && desiredDateTime.day < 7) {
          prevYear = desiredDateTime.minus({ years: 1 });
        } else {
          prevYear = desiredDateTime;
        }

        if (prevDesired.month === prevMonth.month) {
          ClubRouter.replaceClubRoute(club, 'matches.month', {
            year: prevDesired.year,
            month: prevDesired.month,
            teamId,
            eventType,
          });
        }
        // Set the desired day and month
        setDesiredDay(prevDesired.day);
        setDesiredMonth(prevDesired.month);
        setDesiredYear(prevYear.year); // Update the year here
      }}
    >
      {content}
    </button>
  );
  const wrapNext = (content) => (
    <button
      type="button"
      onClick={() => {
        // If we go forward a week from desired day, are we in the next month?
        // If so, set the URL to the next month
        const nextDesired = desiredDateTime.plus({ weeks: 1 });
        const nextMonth = desiredDateTime.plus({ months: 1 });
        let nextYear;
        if (desiredDateTime.month === 12 && desiredDateTime.day > 24) {
          nextYear = desiredDateTime.plus({ years: 1 });
        } else {
          nextYear = desiredDateTime;
        }

        if (nextDesired.month === nextMonth.month) {
          ClubRouter.replaceClubRoute(club, 'matches.month', {
            year: nextDesired.year,
            month: nextDesired.month,
            teamId,
            eventType,
          });
        }
        // Set the new desired day and month
        setDesiredDay(nextDesired.day);
        setDesiredMonth(nextDesired.month);
        setDesiredYear(nextYear.year); // Update the year here
      }}
    >
      {content}
    </button>
  );

  if (placement === 'top') {
    const dateString = `
      ${firstValidDateTime.toFormat('ccc d LLL')} -
      ${lastValidDateTime.toFormat('ccc d LLL')}
    `;
    return (
      <Rectangle
        fill="#000"
        style={{ marginBottom: '20px' }}
        responsive={[{ minWidth: 'fullHeader', props: { radius: '10px' } }]}
      >
        <FlexContainer style={{ padding: '20px 0', gap: '20px' }} alignItems="center">
          {wrapPrev(
            <Typography style={{ padding: '10px' }}>
              <Icon name="arrow-left" color="#fff" size={20} topOffset={2} />
            </Typography>,
          )}
          {validDates.includes(DateTime.now().toFormat('yyyy-MM-dd')) ? (
            <FlexContainer
              flexDirection="column"
              alignItems="center"
              style={{ flex: 1, gap: '10px' }}
            >
              <Typography color="#fff" preset="description" size={20} weight="bold">
                This Week
              </Typography>
              <Typography color="#fff" preset="description" size={18}>
                {dateString}
              </Typography>
            </FlexContainer>
          ) : (
            <Typography
              color="#fff"
              preset="description"
              align="center"
              size={20}
              style={{ flex: 1 }}
            >
              {dateString}
            </Typography>
          )}
          {wrapNext(
            <Typography style={{ padding: '10px' }}>
              <Icon name="arrow-right" color="#fff" size={20} topOffset={2} />
            </Typography>,
          )}
        </FlexContainer>
      </Rectangle>
    );
  }

  return (
    <FlexContainer
      alignItems="center"
      justifyContent="space-between"
      style={{ width: '100%', padding: '20px 10px', boxSizing: 'border-box' }}
    >
      {wrapPrev(
        <Typography color="#000" weight="bold">
          <Cushion right="medium">
            <Icon name="arrow-left" size={20} topOffset={4} />
          </Cushion>
          Previous Week
        </Typography>,
      )}
      {wrapNext(
        <Typography color="#000" weight="bold">
          Next Week
          <Cushion left="medium">
            <Icon name="arrow-right" size={20} topOffset={4} />
          </Cushion>
        </Typography>,
      )}
    </FlexContainer>
  );
};

NextAndPreviousControls.defaultProps = {
  eventType: null,
  teamId: null,
};

NextAndPreviousControls.propTypes = {
  eventType: PropTypes.oneOf(['fixtures', 'training']),
  month: PropTypes.number.isRequired,
  teamId: PropTypes.number,
  year: PropTypes.number.isRequired,
  desiredDay: PropTypes.number.isRequired,
  setDesiredDay: PropTypes.func.isRequired,
  setDesiredMonth: PropTypes.func.isRequired,
  setDesiredYear: PropTypes.func.isRequired,
  validDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom']).isRequired,
};

export default NextAndPreviousControls;
